// generic-form.service.ts
import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root',
})
export class ReactiveFormsService {
	private fb = inject(FormBuilder);
	private trans = inject(TranslateService);

	/**
	 * Inicializa un formulario reactivo con valores iniciales y validadores.
	 * @param initialValues Valores iniciales del formulario.
	 * @param validators Validadores para cada campo del formulario.
	 * @returns FormGroup inicializado.
	 */
	initializeForm(initialValues: { [key: string]: any }, validators: { [key: string]: any } = {}): FormGroup {
		const controlsConfig: { [key: string]: any } = {};

		// Itera sobre los valores iniciales y configura los controles con sus validadores
		for (const key in initialValues) {
			if (Object.prototype.hasOwnProperty.call(initialValues, key)) {
				controlsConfig[key] = [initialValues[key], validators[key]];
			}
		}

		return this.fb.group(controlsConfig);
	}

	/**
	 * Reinicia un formulario a sus valores iniciales.
	 * @param form FormGroup que se va a reiniciar.
	 * @param initialValues Valores iniciales del formulario.
	 */
	resetForm(form: FormGroup, initialValues: { [key: string]: any }): void {
		form.reset(initialValues);
	}

	/**
	 * Obtiene el valor actual del formulario.
	 * @param form FormGroup del cual se obtendrá el valor.
	 * @returns Valor actual del formulario.
	 */
	getFormValue(form: FormGroup): any {
		return form.value;
	}

	/**
	 * Realiza la validación y marca los controles como tocados.
	 * @param form FormGroup que se va a validar.
	 */
	validateForm(form: FormGroup): void {
		// Itera sobre los controles, los marca como tocados y actualiza la validez
		Object.keys(form.controls).forEach((controlName) => {
			form.get(controlName).markAsTouched();
			form.get(controlName).updateValueAndValidity();
		});
	}

	/**
	 * Verifica si un control específico tiene un error.
	 * @param form FormGroup que contiene el control.
	 * @param control Nombre del control a verificar.
	 * @param error Tipo de error a verificar.
	 * @returns True si el control ha sido tocado y tiene el error especificado.
	 */
	hasError(control: any, error?: string): boolean {
		if (error) {
			return control.touched && control.hasError(error);
		}

		return control.touched && control.invalid;
	}

	public hasMsgError(control: Partial<FormControl>, error?: string): string {
		if (this.hasError(control, error)) {
			if (control?.errors?.['required']) {
				return this.trans.instant('GENERAL.VALIDATION.REQUIRED');
			}
			if (
				control?.errors?.['incorrectFormat'] ||
				control?.errors?.['pattern'] ||
				control?.errors?.['phoneE164']
			) {
				return this.trans.instant('GENERAL.VALIDATION.INCORRECT_FORMAT');
			}
			if (control?.errors?.['minlength']) {
				return this.trans.instant('GENERAL.VALIDATION.EXACT_CHARACTERS', {
					characters: control?.errors?.['minlength'].requiredLength,
				});
			}
			if (control?.errors?.['maxlength']) {
				return this.trans.instant('GENERAL.VALIDATION.EXACT_CHARACTERS', {
					characters: control?.errors?.['maxlength'].requiredLength,
				});
			}
			if (control?.errors?.['pattern']) {
				return this.trans.instant('GENERAL.VALIDATION.INCORRECT_FORMAT');
			}
		}
		return '';
	}
}
